import {DataStatus, showNotification} from 'platform/components';

import {useDispatch} from 'react-redux';

import {find, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  customerApi,
  PersonRequestBody,
  PersonResponseBodyV2,
  useCreateCustomerContactV2Mutation,
  useGetCustomerV2Query,
  usePatchCustomerContactV2Mutation,
} from '@dms/api/customer';
import {useGetBusinessCaseQuery, usePutBusinessCaseContactPersonMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  FORM_ROOT_ERROR_NAME,
  getApiValidationErrors,
  getContactPersonPrimaryOptionFormatter,
  getEmailsRequestBody,
  handleApiError,
  mapCustomerValidationErrors,
  usePermissions,
} from '@dms/shared';
import {AutoSave, Form, personRequestToResponse} from '@dms/teas';

import {ApiError, buildArray, Nullish, useRequiredParams} from 'shared/web';

import {PersonSelect} from '../../../../components/Person/components/PersonSelect';
import {PersonSubmissionError} from '../../../../components/Person/constants/PersonSubmissionError';

interface ContactPersonFormProps {
  customerId: string;
  contactPersonId?: string | Nullish;
  isDisabled?: boolean;
}

type BusinessCaseContactPersonRequestBody = {
  contactId: string | Nullish;
};

const getSubmissionErrors = (errors: NonNullable<ReturnType<typeof getApiValidationErrors>>) => {
  const emails: ({email: string} | undefined)[] = [];
  for (const {name, message} of errors) {
    const [, index] = name.split('.');
    emails[Number(index)] = {email: message};
  }
  return {emails};
};

// Refusals outside e-mail rows still need a toast; the default toast omits validationErrors.
const handleContactSaveError = (error: ApiError) => {
  handleApiError(error, {
    silentOnValidationError: true,
    callback: ({validationErrors}) => {
      if (isNotNil(validationErrors)) {
        showNotification.error(validationErrors.map(({message}) => message).join(' '));
      }
    },
  });
  throw error;
};

export function ContactPersonForm(props: ContactPersonFormProps) {
  const [canEditCustomer] = usePermissions({permissionKeys: ['editCustomer']});
  const {id: businessCaseId} = useRequiredParams();
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});

  const {
    data: customer,
    isLoading,
    isError,
  } = useGetCustomerV2Query({customerId: props.customerId});

  const [patchCustomerContact] = usePatchCustomerContactV2Mutation();
  const [createCustomerContact] = useCreateCustomerContactV2Mutation();
  const [putBusinessCaseContactPerson] = usePutBusinessCaseContactPersonMutation();
  const customerContacts = customer?.contacts ?? [];

  const contactPerson = find((person) => person.id === props.contactPersonId, customerContacts);

  const primaryContactIds = new Set(buildArray<string>().whenNotNil(customer?.primaryContactId));

  const dispatch = useDispatch();

  const handleSubmit = (requestBody: BusinessCaseContactPersonRequestBody) => {
    if (typeof requestBody.contactId === 'string') {
      putBusinessCaseContactPerson({
        businessCaseId: businessCase!.id,
        putBusinessCaseContactPersonRequestBody: {
          contactId: requestBody.contactId,
        },
      })
        .unwrap()
        .then(() => {
          dispatch(customerApi.util.invalidateTags([{type: 'Customer', id: props.customerId}]));
        })
        .catch(handleApiError);
    }
  };

  const handlePersonSubmit = (
    personFormValues: PersonRequestBody,
    contactId: string | null
  ): Promise<PersonResponseBodyV2 | null> => {
    const {emails, formIndexes} = getEmailsRequestBody(personFormValues.emails);
    const personRequestBody = {
      ...personFormValues,
      emails,
    };
    const assignContactPerson = (savedContactId: string) =>
      putBusinessCaseContactPerson({
        businessCaseId: businessCase!.id,
        putBusinessCaseContactPersonRequestBody: {
          contactId: savedContactId,
        },
      })
        .unwrap()
        .then(() => personRequestToResponse(savedContactId, customer?.addresses, personRequestBody))
        .catch((error: Error) => {
          handleApiError(error);
          return null;
        });

    const saveContact = isNotNil(contactId)
      ? patchCustomerContact({customerId: props.customerId, contactId, personRequestBody})
          .unwrap()
          .then(() => contactId)
      : createCustomerContact({customerId: props.customerId, personRequestBody})
          .unwrap()
          .then(({id}) => id);

    return saveContact.then(assignContactPerson).catch((error: ApiError) => {
      const errors = mapCustomerValidationErrors(getApiValidationErrors(error), {
        emailsPath: 'emails',
        emailFormIndexes: formIndexes,
      });
      if (errors?.length && !errors.some(({name}) => name === FORM_ROOT_ERROR_NAME)) {
        throw new PersonSubmissionError(getSubmissionErrors(errors));
      }
      return handleContactSaveError(error);
    });
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} isEmpty={isNilOrEmpty(customer)}>
      <Form<BusinessCaseContactPersonRequestBody>
        formId={testIds.businessCase.customer('contactForm')}
        onSubmit={handleSubmit}
        initialValues={{contactId: contactPerson?.id}}
        isFormSaverDisabled
        render={() => (
          <>
            <AutoSave save={handleSubmit} saveOnChange />
            <PersonSelect<BusinessCaseContactPersonRequestBody>
              name="contactId"
              label={i18n.t('entity.person.selectPerson')}
              handlePersonSubmit={handlePersonSubmit}
              initialValue={contactPerson}
              person={customerContacts}
              formatOptionLabel={getContactPersonPrimaryOptionFormatter(primaryContactIds)}
              inlineEdit
              showPersonInfo
              showPhoneNumbers
              showEmails
              showRole
              isDisabled={props.isDisabled}
              isEditingDisabled={!canEditCustomer}
            />
          </>
        )}
      />
    </DataStatus>
  );
}
